<template>
	<div
			:class="meta.mainContainerClass"
			style="background: #f7f7f7; min-height: 100%"
	>
		<div class="top-toolbar fill-width white">
			<v-container class="py-1 d-flex flex-row align-center">
				<router-link :to="{ name: 'main' }">
					<logo-component
							:height="41"
							:short="$vuetify.breakpoint.smAndDown"
					/>
				</router-link>
				<v-spacer />

				<template v-if="currentUser">
					<template v-if="$vuetify.breakpoint.mdAndUp">
						<div class="tabs-wrapper dense mr-4">
							<router-link
									:to="{ name: 'feed' }"
									active-class="active"
									class="tab-item"
									exact
							>
								<div class="tab-item__icon">
									<v-icon>mdi-home</v-icon>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.home") }}
								</div>
							</router-link>
							<router-link
									:to="{ name: 'connections' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-badge
											:content="connectionsCount"
											:value="connectionsCount > 0"
											bordered
											offset-x="10"
											offset-y="10"
									>
										<v-icon>mdi-account-multiple</v-icon>
									</v-badge>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.myNetwork") }}
								</div>
							</router-link>
							<router-link
									:to="{ name: 'lessons' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-icon>mdi-school</v-icon>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.lessons") }}
								</div>
							</router-link>
							<router-link
									:to="{ name: 'materials' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-icon>mdi-book-education</v-icon>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.materials") }}
								</div>
							</router-link>
							<router-link
									v-if="$vuetify.breakpoint.mdAndUp"
									:to="{ name: 'lesson-boards' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-icon>mdi-bulletin-board</v-icon>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.lessonBoards") }}
								</div>
							</router-link>
							<router-link
									:to="{ name: 'messages' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-badge
											:content="messagesCount"
											:value="messagesCount > 0"
											bordered
											offset-x="10"
											offset-y="10"
									>
										<v-icon>mdi-email</v-icon>
									</v-badge>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.messages") }}
								</div>
							</router-link>
							<router-link
									:to="{ name: 'notifications' }"
									active-class="active"
									class="tab-item"
							>
								<div class="tab-item__icon">
									<v-badge
											:content="notificationsCount"
											:value="notificationsCount > 0"
											bordered
											offset-x="10"
											offset-y="10"
									>
										<v-icon>mdi-bell</v-icon>
									</v-badge>
								</div>
								<div class="tab-item__text">
									{{ $t("pages.common.tabs.notifications") }}
								</div>
							</router-link>
						</div>

						<v-menu offset-y>
							<template #activator="{ on, attrs }">
								<div
										v-bind="attrs"
										v-on="on"
								>
									<UserAvatarComponent
											:size="38"
											:user="currentUser"
											class="cursor-pointer"
											profile-image-key="profilePhotoX64"
									/>
								</div>
							</template>

							<v-card
									:rounded="$vuetify.breakpoint.smAndDown ? '0' : 'lg'"
									class="pt-4"
									style="width: 280px"
							>
								<div class="px-4">
									<div class="d-flex flex-row">
										<div class="flex-grow-0">
											<UserAvatarComponent
													:size="58"
													:user="currentUser"
													profile-image-key="profilePhotoX64"
											/>
										</div>
										<div class="flex-grow-1 d-flex justify-center align-center">
											<div
													class="text-h6 text-center"
													style="line-height: 1.6rem"
											>
												{{ currentUser.firstName }} {{ currentUser.lastName }}
											</div>
										</div>
									</div>
									<div class="text-center mt-3">
										<v-btn
												:to="{
													name: 'userProfile',
													params: { userId: currentUser.id },
												}"
												class="no-uppercase font-weight-medium fill-width"
												color="secondary"
												outlined
												rounded
												small
										>
											<v-icon
													left
													small
											>
												mdi-eye
											</v-icon>
											{{ $t("pages.common.visitProfile") }}
										</v-btn>
									</div>
								</div>

								<v-divider class="mt-4" />

								<div>
									<v-list
											dense
											expand
									>
										<v-list-item :to="{ name: 'preferences' }">
											<v-list-item-icon class="mr-2">
												<v-icon>mdi-cog-outline</v-icon>
											</v-list-item-icon>

											<v-list-item-content>
												<v-list-item-title>
													{{ $t("pages.common.profileSettings") }}
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
										<v-divider class="my-2" />
										<v-list-item :to="{ name: 'logout' }">
											<v-list-item-icon class="mr-2">
												<v-icon>mdi-logout-variant</v-icon>
											</v-list-item-icon>

											<v-list-item-content>
												<v-list-item-title>
													{{ $t("pages.common.logout") }}
												</v-list-item-title>
											</v-list-item-content>
										</v-list-item>
									</v-list>
								</div>
							</v-card>
						</v-menu>
					</template>
					<template v-else>
						<div class="d-flex gap-4">
							<v-btn
									:to="{ name: 'notifications' }"
									icon
							>
								<v-badge
										:content="notificationsCount"
										:value="notificationsCount > 0"
										bordered
										offset-x="10"
										offset-y="10"
								>
									<v-icon :size="30">
										mdi-bell-outline
									</v-icon>
								</v-badge>
							</v-btn>

							<v-btn
									:to="{ name: 'messages' }"
									icon
							>
								<v-badge
										:content="messagesCount"
										:value="messagesCount > 0"
										bordered
										offset-x="10"
										offset-y="10"
								>
									<v-icon :size="30">
										mdi-email-outline
									</v-icon>
								</v-badge>
							</v-btn>

							<v-btn
									v-if="
										$route.name === 'userProfile' &&
											parseInt($route.params.userId) === currentUser.id
									"
									:to="{ name: 'preferences' }"
									icon
							>
								<v-icon :size="30">
									mdi-cog-outline
								</v-icon>
							</v-btn>
						</div>
					</template>
				</template>
				<template v-else>
					<v-btn
							:to="{ name: 'signUp' }"
							class="no-uppercase font-weight-medium"
							color="primary"
							outlined
							rounded
					>
						<v-icon left>
							mdi-account
						</v-icon>
						{{ $t("pages.common.signUp") }}
					</v-btn>
				</template>
			</v-container>
		</div>

		<div
				v-if="$vuetify.breakpoint.smAndDown && currentUser"
				class="bottom-toolbar d-flex flex-row align-center elevation-1 fill-width white pb-3 pt-1"
		>
			<div class="tabs-wrapper">
				<router-link
						:to="{ name: 'feed' }"
						active-class="active"
						class="tab-item"
						exact
				>
					<div class="tab-item__icon">
						<v-icon>mdi-home</v-icon>
					</div>
					<div class="tab-item__text">
						{{ $t("pages.common.tabs.home") }}
					</div>
				</router-link>
				<router-link
						:to="{ name: 'connections' }"
						active-class="active"
						class="tab-item"
				>
					<div class="tab-item__icon">
						<v-badge
								:content="connectionsCount"
								:value="connectionsCount > 0"
								bordered
								offset-x="10"
								offset-y="10"
						>
							<v-icon>mdi-account-multiple</v-icon>
						</v-badge>
					</div>
					<div class="tab-item__text">
						{{ $t("pages.common.tabs.myNetwork") }}
					</div>
				</router-link>
				<router-link
						:to="{ name: 'lessons' }"
						active-class="active"
						class="tab-item"
				>
					<div class="tab-item__icon">
						<v-icon>mdi-school</v-icon>
					</div>
					<div class="tab-item__text">
						{{ $t("pages.common.tabs.lessons") }}
					</div>
				</router-link>
				<router-link
						:to="{ name: 'materials' }"
						active-class="active"
						class="tab-item"
				>
					<div class="tab-item__icon">
						<v-icon>mdi-book-education</v-icon>
					</div>
					<div class="tab-item__text">
						{{ $t("pages.common.tabs.materials") }}
					</div>
				</router-link>
				<router-link
						v-if="$vuetify.breakpoint.mdAndUp"
						:to="{ name: 'lesson-boards' }"
						active-class="active"
						class="tab-item"
				>
					<div class="tab-item__icon">
						<v-icon>mdi-bulletin-board</v-icon>
					</div>
					<div class="tab-item__text">
						{{ $t("pages.common.tabs.lessonBoards") }}
					</div>
				</router-link>
				<router-link
						:to="{ name: 'userProfile', params: { userId: currentUser.id } }"
						active-class="active"
						class="tab-item"
				>
					<div class="tab-item__icon">
						<UserAvatarComponent
								:size="34"
								:user="currentUser"
								class="cursor-pointer mt-1"
								profile-image-key="profilePhotoX64"
						/>
					</div>
				</router-link>
			</div>
		</div>

		<router-view class="toolbar-content-wrapper" />

		<urgent-notification-wrapper />
		<!--		<install-pwa-app-component />-->
	</div>
</template>

<script>
// import InstallPwaAppComponent from "@/components/InstallPWAAppComponent.vue";
import UrgentNotificationWrapper from "@/components/profile/notification/urgent/UrgentNotificationWrapper.vue";
import LogoComponent from "@/components/LogoComponent.vue";
import { mapGetters } from "vuex";
import UserAvatarComponent from "@/components/profile/UserAvatarComponent.vue";
import { routerMetaDataMixin } from "@/mixins/router-meta-data.mixin";

const UPDATE_NOTIFICATION_BADGES_INTERVAL = 20000;
export default {
  name: "MainLayout",
  components: {
    UserAvatarComponent,
    LogoComponent,
    UrgentNotificationWrapper,
    // InstallPwaAppComponent,
  },
  mixins: [routerMetaDataMixin(["mainContainerClass"])],
  data() {
    return {
      notificationsUpdateInterval: null,
    };
  },
  computed: {
    ...mapGetters({
      currentUser: "getCurrentUser",
      notificationComputedBadges: "getNotificationComputedBadges",
      messagesCount: "getMessagesCount",
      connectionsCount: "getConnectionsCount",
    }),
    notificationsCount() {
      if (!this.notificationComputedBadges) return null;
      return this.notificationComputedBadges[null];
    },
  },
  watch: {
    async notificationsCount(current, old) {
      if (old === null) return;
      if (current <= old) return;

      await this.$store.dispatch("playNewNotificationSound");
    },
    async messagesCount(current, old) {
      if (old === null) return;
      if (current <= old) return;

      await this.$store.dispatch("playNewMessageSound");
    },
    async connectionsCount(current, old) {
      if (old === null) return;
      if (current <= old) return;

      await this.$store.dispatch("playNewConnectionSound");
    },
  },
  async mounted() {
    if (this.currentUser) {
      await this.$store.dispatch("updateCurrentUser");
      await this.$store.dispatch("updateNotificationCountQuery");

      this.notificationsUpdateInterval = setInterval(async () => {
        await this.$store.dispatch("updateNotificationCountQuery");
      }, UPDATE_NOTIFICATION_BADGES_INTERVAL);
    }
  },
  beforeDestroy() {
    if (this.notificationsUpdateInterval) {
      clearInterval(this.notificationsUpdateInterval);
    }
  },
};
</script>

<style lang="scss" scoped>
.top-toolbar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2;
  border-bottom: 1px solid #cecece !important;
}

.bottom-toolbar {
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 1;
}

.toolbar-content-wrapper {
  padding-top: 50px;
  padding-bottom: 0;

  @media (max-width: 959px) {
    padding-bottom: 60px;
  }
}
</style>
